/**
 * Device management page for organization members.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { config } from '../config';

interface Device {
  id: string;
  name: string;
  description: string | null;
  organization_id: string;
  organization_name: string | null;
  service_id: string;
  service_name: string | null;
  device_type: string | null;
  hardware_id: string | null;
  firmware_version: string | null;
  metadata: Record<string, unknown>;
  token_prefix: string | null;
  status: 'active' | 'suspended' | 'revoked';
  created_at: string;
  created_by: string | null;
  last_seen_at: string | null;
}

interface Service {
  id: string;
  name: string;
}

interface DeviceType {
  type: string;
  category: string | null;
  label: string;
  description: string;
}

export default function Devices() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, currentOrganization } = useAuth();
  const [devices, setDevices] = useState<Device[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state for creating device
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceDescription, setNewDeviceDescription] = useState('');
  const [newDeviceServiceId, setNewDeviceServiceId] = useState('');
  const [newDeviceType, setNewDeviceType] = useState('');
  const [newDeviceHardwareId, setNewDeviceHardwareId] = useState('');
  const [createdDeviceToken, setCreatedDeviceToken] = useState<string | null>(null);

  // Modal state for viewing device details
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login?return_to=/devices', { replace: true });
      return;
    }

    if (currentOrganization) {
      loadDevices();
      loadServices();
      loadDeviceTypes();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading, currentOrganization, navigate]);

  const loadDevices = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      const response = await fetch(
        `${config.gatewayUrl}/v1/organizations/${currentOrganization.id}/devices`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setDevices(data.devices || []);
      } else if (response.status === 403) {
        setError('You do not have permission to view devices for this organization.');
      } else {
        setError('Failed to load devices');
      }
    } catch (err) {
      setError('Failed to load devices');
    } finally {
      setIsLoading(false);
    }
  };

  const loadServices = async () => {
    if (!currentOrganization) return;

    try {
      const response = await fetch(
        `${config.gatewayUrl}/v1/organizations/${currentOrganization.id}/services`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setServices(data.services || []);
      }
    } catch (err) {
      // Services list is optional, don't show error
    }
  };

  const loadDeviceTypes = async () => {
    try {
      const response = await fetch(`${config.gatewayUrl}/v1/device-types`);
      if (response.ok) {
        const data = await response.json();
        setDeviceTypes(data.device_types || []);
      }
    } catch (err) {
      // Device types are optional, don't show error
    }
  };

  const handleCreateDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization || !newDeviceName.trim() || !newDeviceServiceId) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/organizations/${currentOrganization.id}/devices`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newDeviceName.trim(),
          description: newDeviceDescription.trim() || null,
          service_id: newDeviceServiceId,
          device_type: newDeviceType.trim() || null,
          hardware_id: newDeviceHardwareId.trim() || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedDeviceToken(data.token);
        loadDevices();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create device');
      }
    } catch (err) {
      setError('Failed to create device');
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewDeviceName('');
    setNewDeviceDescription('');
    setNewDeviceServiceId('');
    setNewDeviceType('');
    setNewDeviceHardwareId('');
    setCreatedDeviceToken(null);
  };

  const suspendDevice = async (deviceId: string, deviceName: string) => {
    if (!confirm(`Suspend device "${deviceName}"? It will no longer be able to authenticate.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/devices/${deviceId}/suspend`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        loadDevices();
        if (selectedDevice?.id === deviceId) {
          setSelectedDevice({ ...selectedDevice, status: 'suspended' });
        }
      } else {
        setError('Failed to suspend device');
      }
    } catch (err) {
      setError('Failed to suspend device');
    }
  };

  const reactivateDevice = async (deviceId: string) => {
    try {
      const response = await fetch(`${config.gatewayUrl}/v1/devices/${deviceId}/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        loadDevices();
        if (selectedDevice?.id === deviceId) {
          setSelectedDevice({ ...selectedDevice, status: 'active' });
        }
      } else {
        setError('Failed to reactivate device');
      }
    } catch (err) {
      setError('Failed to reactivate device');
    }
  };

  const revokeDevice = async (deviceId: string, deviceName: string) => {
    if (!confirm(`Revoke device "${deviceName}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/devices/${deviceId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        loadDevices();
        if (selectedDevice?.id === deviceId) {
          setShowDetailsModal(false);
          setSelectedDevice(null);
        }
      } else {
        setError('Failed to revoke device');
      }
    } catch (err) {
      setError('Failed to revoke device');
    }
  };

  const regenerateDeviceToken = async (deviceId: string, deviceName: string) => {
    if (!confirm(`Regenerate token for "${deviceName}"? The old token will stop working immediately.`)) return;

    try {
      const response = await fetch(`${config.gatewayUrl}/v1/devices/${deviceId}/regenerate-token`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedDeviceToken(data.token);
        setShowDetailsModal(false);
        setShowCreateModal(true);
        loadDevices();
      } else {
        setError('Failed to regenerate token');
      }
    } catch (err) {
      setError('Failed to regenerate token');
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (!currentOrganization) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-content-primary mb-2">No Organization Selected</h1>
          <p className="text-content-secondary mb-4">Please select an organization to manage devices.</p>
          <Link to="/organizations" className="btn-primary inline-block">
            Select Organization
          </Link>
        </div>
      </div>
    );
  }

  const activeDevices = devices.filter(d => d.status === 'active').length;
  const suspendedDevices = devices.filter(d => d.status === 'suspended').length;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">Devices</h1>
          <p className="text-content-secondary mt-1">
            Manage IoT and hardware devices for <span className="font-medium">{currentOrganization.name}</span>
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="card text-center">
            <div className="text-2xl font-bold text-orbital-600">{devices.length}</div>
            <div className="text-sm text-content-secondary">Total Devices</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-green-600">{activeDevices}</div>
            <div className="text-sm text-content-secondary">Active</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-yellow-600">{suspendedDevices}</div>
            <div className="text-sm text-content-secondary">Suspended</div>
          </div>
        </div>

        {/* Device List */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-content-primary">Registered Devices</h2>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary text-sm">
              Register Device
            </button>
          </div>

          {devices.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-content-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              <p className="text-content-tertiary mt-4 mb-2">No devices registered yet.</p>
              <p className="text-sm text-content-tertiary">Register a device to authenticate IoT/hardware systems.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Service</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Last Seen</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-content-tertiary uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {devices.map((device) => (
                    <tr key={device.id}>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => { setSelectedDevice(device); setShowDetailsModal(true); }}
                          className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 font-medium hover:underline"
                        >
                          {device.name}
                        </button>
                        {device.description && (
                          <p className="text-content-tertiary text-xs mt-0.5 truncate max-w-xs">{device.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-content-secondary">
                        {device.device_type
                          ? deviceTypes.find(t => t.type === device.device_type)?.label || device.device_type
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-content-secondary">{device.service_name || device.service_id}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          device.status === 'active' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' :
                          device.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                        }`}>
                          {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-content-tertiary">
                        {device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-4 py-3 space-x-2">
                        {device.status === 'active' && (
                          <button
                            onClick={() => suspendDevice(device.id, device.name)}
                            className="text-sm text-yellow-600 hover:text-yellow-800"
                          >
                            Suspend
                          </button>
                        )}
                        {device.status === 'suspended' && (
                          <button
                            onClick={() => reactivateDevice(device.id)}
                            className="text-sm text-green-600 hover:text-green-800"
                          >
                            Reactivate
                          </button>
                        )}
                        {device.status !== 'revoked' && (
                          <button
                            onClick={() => revokeDevice(device.id, device.name)}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Device Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-md w-full p-6">
              {createdDeviceToken ? (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">Device Token Created</h3>
                  <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800 dark:text-green-300 mb-2 font-medium">Save this token - it won't be shown again!</p>
                    <code className="block bg-surface-primary border rounded p-2 text-sm break-all select-all">
                      {createdDeviceToken}
                    </code>
                  </div>
                  <p className="text-sm text-content-tertiary mb-4">
                    Use this token in the <code className="bg-surface-tertiary px-1 rounded">X-Device-Token</code> header when calling the API.
                  </p>
                  <button onClick={closeCreateModal} className="btn-primary w-full">
                    Done
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-content-primary mb-4">Register Device</h3>
                  <form onSubmit={handleCreateDevice} className="space-y-4">
                    <div>
                      <label htmlFor="deviceName" className="block text-sm font-medium text-content-primary mb-1">
                        Device Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="deviceName"
                        type="text"
                        value={newDeviceName}
                        onChange={(e) => setNewDeviceName(e.target.value)}
                        placeholder="e.g., Temperature Sensor #1"
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="deviceService" className="block text-sm font-medium text-content-primary mb-1">
                        Service <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="deviceService"
                        value={newDeviceServiceId}
                        onChange={(e) => setNewDeviceServiceId(e.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="">Select service...</option>
                        {services.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                      </select>
                      {services.length === 0 && (
                        <p className="text-xs text-yellow-600 mt-1">No services available. Contact your administrator.</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="deviceType" className="block text-sm font-medium text-content-primary mb-1">
                        Device Type
                      </label>
                      <select
                        id="deviceType"
                        value={newDeviceType}
                        onChange={(e) => setNewDeviceType(e.target.value)}
                        className="input-field"
                      >
                        <option value="">Select type...</option>
                        {/* Group by category */}
                        {deviceTypes.filter(t => t.category === 'iot').length > 0 && (
                          <optgroup label="IoT Devices">
                            {deviceTypes.filter(t => t.category === 'iot').map(t => (
                              <option key={t.type} value={t.type}>{t.label}</option>
                            ))}
                          </optgroup>
                        )}
                        {deviceTypes.filter(t => t.category === 'infrastructure').length > 0 && (
                          <optgroup label="Infrastructure">
                            {deviceTypes.filter(t => t.category === 'infrastructure').map(t => (
                              <option key={t.type} value={t.type}>{t.label}</option>
                            ))}
                          </optgroup>
                        )}
                        {deviceTypes.filter(t => t.category === 'client').length > 0 && (
                          <optgroup label="Client Software">
                            {deviceTypes.filter(t => t.category === 'client').map(t => (
                              <option key={t.type} value={t.type}>{t.label}</option>
                            ))}
                          </optgroup>
                        )}
                        {deviceTypes.filter(t => !t.category).length > 0 && (
                          <optgroup label="Other">
                            {deviceTypes.filter(t => !t.category).map(t => (
                              <option key={t.type} value={t.type}>{t.label}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="deviceHardwareId" className="block text-sm font-medium text-content-primary mb-1">
                        Hardware ID
                      </label>
                      <input
                        id="deviceHardwareId"
                        type="text"
                        value={newDeviceHardwareId}
                        onChange={(e) => setNewDeviceHardwareId(e.target.value)}
                        placeholder="e.g., serial number, MAC address"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label htmlFor="deviceDesc" className="block text-sm font-medium text-content-primary mb-1">
                        Description
                      </label>
                      <textarea
                        id="deviceDesc"
                        value={newDeviceDescription}
                        onChange={(e) => setNewDeviceDescription(e.target.value)}
                        placeholder="Optional description..."
                        className="input-field"
                        rows={2}
                      />
                    </div>
                    <div className="flex space-x-3">
                      <button type="button" onClick={closeCreateModal} className="flex-1 btn-secondary">
                        Cancel
                      </button>
                      <button type="submit" disabled={services.length === 0} className="flex-1 btn-primary disabled:opacity-50">
                        Create Device
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* Device Details Modal */}
        {showDetailsModal && selectedDevice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-primary rounded-lg max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-content-primary">{selectedDevice.name}</h3>
                <button
                  onClick={() => { setShowDetailsModal(false); setSelectedDevice(null); }}
                  className="text-content-tertiary hover:text-content-secondary"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-content-tertiary">Status</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    selectedDevice.status === 'active' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' :
                    selectedDevice.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                  }`}>
                    {selectedDevice.status.charAt(0).toUpperCase() + selectedDevice.status.slice(1)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-content-tertiary block">Service</span>
                    <span className="font-medium">{selectedDevice.service_name || selectedDevice.service_id}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Type</span>
                    <span className="font-medium">
                      {selectedDevice.device_type
                        ? deviceTypes.find(t => t.type === selectedDevice.device_type)?.label || selectedDevice.device_type
                        : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Hardware ID</span>
                    <span className="font-medium font-mono text-xs">{selectedDevice.hardware_id || '-'}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Token Prefix</span>
                    <span className="font-medium font-mono">{selectedDevice.token_prefix ? `${selectedDevice.token_prefix}...` : '-'}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Last Seen</span>
                    <span className="font-medium">
                      {selectedDevice.last_seen_at ? new Date(selectedDevice.last_seen_at).toLocaleString() : 'Never'}
                    </span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Created</span>
                    <span className="font-medium">{new Date(selectedDevice.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-content-tertiary block">Firmware</span>
                    <span className="font-medium">{selectedDevice.firmware_version || '-'}</span>
                  </div>
                </div>

                {selectedDevice.description && (
                  <div>
                    <span className="text-sm text-content-tertiary block">Description</span>
                    <p className="text-sm">{selectedDevice.description}</p>
                  </div>
                )}

                <div className="border-t pt-4 flex flex-wrap gap-2">
                  {selectedDevice.status !== 'revoked' && (
                    <button
                      onClick={() => regenerateDeviceToken(selectedDevice.id, selectedDevice.name)}
                      className="btn-secondary text-sm"
                    >
                      Regenerate Token
                    </button>
                  )}
                  {selectedDevice.status === 'active' && (
                    <button
                      onClick={() => suspendDevice(selectedDevice.id, selectedDevice.name)}
                      className="text-sm px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200"
                    >
                      Suspend
                    </button>
                  )}
                  {selectedDevice.status === 'suspended' && (
                    <button
                      onClick={() => reactivateDevice(selectedDevice.id)}
                      className="text-sm px-4 py-2 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 rounded-lg hover:bg-green-200"
                    >
                      Reactivate
                    </button>
                  )}
                  {selectedDevice.status !== 'revoked' && (
                    <button
                      onClick={() => revokeDevice(selectedDevice.id, selectedDevice.name)}
                      className="text-sm px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 rounded-lg hover:bg-red-200"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
